import React from "react";
import { style } from "../style";
import styled from "styled-components";
import { Box } from "../Box";
import { useEvent } from "../hooks/useEvent";
import { Icon } from "../Icon";
import { LooseInteractionButton } from "../__Internal__";

const OuterContainer = styled.div`
  display: inline-block;
`;

const InnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  height: 44px;
  width: 44px;
  position: relative;
`;

const TextWrapper = styled.span`
  font-weight: ${style.vars.fontWeight.bold};
`;

type Props = {
  /**
   * The current page number.
   */
  page: number;
  /**
   * The size of the page.
   */
  pageSize: number;
  /**
   * The total number of items.
   */
  totalCount: number;
  /**
   * Whether the input is disabled.
   * @default false
   */
  disabled?: boolean;

  onChange(nextPage: number): void;

  /**
   * Testing utility tag.
   * @default "paginator"
   */
  "data-testid"?: string;
};

export const PaginatorInline: React.FC<Props> = (props) => {
  const { page, pageSize, totalCount, disabled = false } = props;
  const testId = props["data-testid"] ?? "paginator";
  const pageMax = Math.ceil(totalCount / pageSize);
  const isMin = page <= 1;
  const isMax = page >= pageMax;
  const rangeMin = (page - 1) * pageSize + 1;
  const rangeMax = Math.min(page * pageSize, totalCount);

  const handleDecrement = useEvent((ev: React.MouseEvent) => {
    ev.preventDefault();
    const next = page - 1;

    if (next >= 1) {
      props.onChange(next);
    }
  });

  const handleIncrement = useEvent((ev: React.MouseEvent) => {
    ev.preventDefault();
    const next = page + 1;

    if (next <= pageMax) {
      props.onChange(next);
    }
  });

  return (
    <OuterContainer data-testid={testId}>
      <InnerContainer>
        <TextWrapper data-testid={`${testId}/current-range`}>
          {rangeMin}-{rangeMax}
        </TextWrapper>
        <Box ml={1} />
        <TextWrapper>of</TextWrapper>
        <Box ml={1} />
        <TextWrapper data-testid={`${testId}/total`}>{totalCount}</TextWrapper>
        <Box ml={2} />
        <ButtonWrapper>
          <LooseInteractionButton
            data-testid={`${testId}/decrement`}
            onClick={handleDecrement}
            disabled={disabled || isMin}
            borderTop={false}
            borderRight={false}
            borderBottom={false}
            borderLeft={false}
            borderRadiusBottomLeft="base"
            borderRadiusBottomRight="base"
            borderRadiusTopLeft="base"
            borderRadiusTopRight="base"
          >
            <Icon icon="arrow-left" />
          </LooseInteractionButton>
        </ButtonWrapper>
        <ButtonWrapper>
          <LooseInteractionButton
            data-testid={`${testId}/increment`}
            onClick={handleIncrement}
            disabled={disabled || isMax}
            borderTop={false}
            borderRight={false}
            borderBottom={false}
            borderLeft={false}
            borderRadiusBottomLeft="base"
            borderRadiusBottomRight="base"
            borderRadiusTopLeft="base"
            borderRadiusTopRight="base"
          >
            <Icon icon="arrow-right" />
          </LooseInteractionButton>
        </ButtonWrapper>
      </InnerContainer>
    </OuterContainer>
  );
};
